import { db } from "./idb.ts";

/**
 * Every write the user makes is applied locally at once and appended here.
 * Nothing in the UI ever waits for a network, and nothing asks the user to
 * sync (FR-11.3).
 *
 * Entries are replayed in order. Each one is idempotent on the server —
 * inserts collide on a unique client_id or natural key and do nothing the
 * second time, patches carry the moment the user made the change and are
 * resolved against it (FR-11.7). So a replay after a half-finished sync is
 * always safe, and the queue never needs to know what got through.
 */
export type OutboxOp =
  | { kind: "insert_sauna"; row: Record<string, unknown>; stove: Record<string, unknown>;
      positions: Record<string, unknown>[] }
  | { kind: "update_sauna"; id: string; patch: Record<string, unknown> }
  | { kind: "upsert_position"; row: Record<string, unknown> }
  | { kind: "insert_session"; row: Record<string, unknown> }
  | { kind: "patch_session"; session_id: string; field: string; value: unknown; changed_at: string }
  | { kind: "insert_measurement"; row: Record<string, unknown>; readings: Record<string, unknown>[] }
  | { kind: "update_readings"; measurement_id: string; readings: Record<string, unknown>[] }
  | { kind: "insert_group"; row: Record<string, unknown> }
  | { kind: "patch_group"; id: string; ended_at: string | null; person_count?: number | null }
  | { kind: "insert_fuel"; row: Record<string, unknown> }
  | { kind: "upsert_rating"; row: Record<string, unknown> }
  | { kind: "insert_comment"; row: Record<string, unknown> }
  | { kind: "insert_stove_event"; row: Record<string, unknown> };

export interface OutboxEntry {
  seq?: number;
  op: OutboxOp;
  queued_at: string;
  attempts: number;
  last_error?: string;
}

export async function enqueue(op: OutboxOp): Promise<void> {
  await (await db()).add("outbox", {
    op, queued_at: new Date().toISOString(), attempts: 0,
  } satisfies OutboxEntry);
}

export async function pending(): Promise<Required<OutboxEntry>[]> {
  return (await db()).getAll("outbox") as Promise<Required<OutboxEntry>[]>;
}

export async function depth(): Promise<number> {
  return (await db()).count("outbox");
}

export async function done(seq: number): Promise<void> {
  await (await db()).delete("outbox", seq);
}

/**
 * A write the server refuses for a reason that will not change — a check
 * constraint, a value it will not accept — must not sit at the head of the
 * queue forever holding everything behind it. After a few attempts it is
 * moved aside, with its reason, and the rest of the queue moves on.
 *
 * It is set aside, not deleted: the row is still on the device, the user is
 * told, and `retryRejected()` puts them all back.
 */
export async function reject(seq: number, message: string): Promise<void> {
  const d = await db();
  const row = (await d.get("outbox", seq)) as OutboxEntry | undefined;
  if (!row) return;
  await d.put("rejected", { ...row, rejected_at: new Date().toISOString(), last_error: message });
  await d.delete("outbox", seq);
}

export async function rejected(): Promise<Required<OutboxEntry>[]> {
  return (await db()).getAll("rejected") as Promise<Required<OutboxEntry>[]>;
}

export async function rejectedCount(): Promise<number> {
  return (await db()).count("rejected");
}

/** Put everything that was set aside back at the end of the queue. */
export async function retryRejected(): Promise<number> {
  const d = await db();
  const rows = (await d.getAll("rejected")) as OutboxEntry[];
  for (const r of rows) {
    await d.add("outbox", { op: r.op, queued_at: r.queued_at, attempts: 0 } satisfies OutboxEntry);
  }
  await d.clear("rejected");
  return rows.length;
}

export async function failed(seq: number, message: string): Promise<void> {
  const d = await db();
  const row = (await d.get("outbox", seq)) as OutboxEntry | undefined;
  if (!row) return;
  row.attempts += 1;
  row.last_error = message;
  await d.put("outbox", row);
}
